import React from 'react'
import styled from '@emotion/styled'
import ReactDOM from 'react-dom'
import { useHostContext } from '@mobi/account/Common'
import { trackNeedAssistance } from '@mobi/account/Common/Analytics'
import ModalPageHeader from '@mobi/account/Common/ModalPageHeader'
import { useHistory } from '@mobi/account/Hooks'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { FeatureIcon, Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { CustomerServiceCentre, CustomerServiceTelephone } from '@mobi/component-library/Base/Link'
import { colors, font, measurements, radius, spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { callCenterPhoneNumber } from '@mobi/utils/phone'
import { AccountNumberDisplay, CrossIcon, List, ListItem } from './VerificationAccountPages.styled'

type Props = {
  betAccountNumber?: number
  useDisableAppHeaderRender?: () => void
  isBetAccountError?: boolean
}

export const VerificationAccountHomePage = ({
  betAccountNumber,
  useDisableAppHeaderRender,
  isBetAccountError,
}: Props) => {
  const { logout } = useHostContext()
  const history = useHistory()
  useDisableAppHeaderRender?.()

  const handleLogout = () => {
    logout?.().then(() => {
      location.reload() //allows kambi client (useKambiClient.ts) global variables and script to reset.
    })
  }
  const handleCallCustomerService = () => history.push(callCenterPhoneNumber)

  const renderError = () => (
    <Page>
      <Flex flexDirection='column' gap={spacing.md}>
        <FeatureIcon type='error' size='xl' />
        <AccountHeader
          title="We weren't able to load your account details"
          description={
            <CustomerServiceCentre prefixText='Please try again in a few minutes. If the issue persists,' />
          }
          alignItems='start'
        />
      </Flex>
      <Flex flexDirection='column' gap={spacing.sm}>
        <Button
          isFullWidth
          color='primary'
          onClick={handleLogout}
          style={{ marginTop: spacing.smx2 }}
        >
          Log Out
        </Button>
        <Button isFullWidth color='tertiary_color' onClick={handleCallCustomerService}>
          Call Customer Service Centre
        </Button>
      </Flex>
    </Page>
  )

  const renderHome = () => (
    <Page style={{ backgroundColor: colors.surface[300] }}>
      <Card>
        <Header>ID Verification Account</Header>
        <CardDescription>
          <span>
            This verification account is limited to identity purposes only and does not allow:
          </span>
          <List style={{ gap: spacing.smx1 }}>
            <ListItem>
              <CrossIcon />
              Online betting
            </ListItem>
            <ListItem>
              <CrossIcon />
              Depositing or withdrawing money
            </ListItem>
            <ListItem>
              <CrossIcon />
              Access to betting bonuses or promotions
            </ListItem>
            <ListItem>
              <CrossIcon />
              Marketing or promotional communications
            </ListItem>
          </List>
        </CardDescription>
        <AccountNumberDisplay>
          <b>{betAccountNumber}</b>
          <div>
            <span>Account Number</span>
          </div>
        </AccountNumberDisplay>
        <CardDescription>
          <div>
            Please keep this number handy. You may be asked to quote this number by TAB venue staff
            in scenarios such as:
            <ul style={{ paddingLeft: spacing.big, marginBottom: 0, marginTop: spacing.smx2 }}>
              <li>Large payouts</li>
              <li>Participation in Punters' Clubs</li>
            </ul>
          </div>
          <ContactUs>
            If you have questions about your verification-only account, or need support, please
            contact us on <CustomerServiceTelephone />
          </ContactUs>
        </CardDescription>
      </Card>
      <Button isFullWidth color='secondary_outline' onClick={handleLogout}>
        <span style={{ color: colors.error[700] }}>Log Out</span>
        <Icon name='DuocolorLogOut02' size={spacing.md} color={colors.error[700]} />
      </Button>
    </Page>
  )

  const content = (
    <Container>
      <ModalPageHeader
        onClickNeedHelp={() => trackNeedAssistance('ID Verification Home')}
        disableNavigation
      />
      <ScrollArea data-testid='verification-account-scroll-area'>
        {isBetAccountError ? renderError() : renderHome()}
      </ScrollArea>
    </Container>
  )

  return ReactDOM.createPortal(content, document.body)
}
const Container = styled.div({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  width: '100%',
  maxWidth: '80rem',
  margin: '0 auto',
  bottom: 0,
  zIndex: 3,
  backgroundColor: colors.white,
  overflow: 'hidden',
})

const ScrollArea = styled.div({
  height: '100%',
  boxSizing: 'border-box',
  paddingTop: measurements.mobi.headerHeight,
  overflowY: 'auto',
  overscrollBehaviorY: 'none',
  WebkitOverflowScrolling: 'touch',
})

const Page = styled.div({
  ...font.size.lg,
  backgroundColor: colors.white,
  display: 'flex',
  flexDirection: 'column',
  padding: `${spacing.sm} ${spacing.md}`,
  rowGap: spacing.sm,
  fontFamily: font.family.primary,
  fontWeight: font.weight.regular,
  color: colors.neutral[900],
  minHeight: '100%',
})

const Header = styled.div({
  backgroundColor: colors.studio[700],
  padding: `${spacing.md}`,
  borderRadius: radius.lg,
  color: colors.white,
  ...font.size.xl2,
  fontWeight: font.weight.semibold,
})

const Card = styled.div({
  backgroundColor: colors.white,
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.bigx1,
  padding: spacing.sm,
  borderRadius: radius.lgx3,
})

const CardDescription = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.big,
  padding: `0 ${spacing.sm} ${spacing.sm}`,
})

const ContactUs = styled.span({
  '& a': {
    color: 'inherit',
  },
})
